import { IFilter } from "@saleor/components/Filter";
import { AutocompleteFilterOpts, FilterOpts, MinMax } from "@saleor/types";
import {
  createAutocompleteField,
  createDateField,
  createOptionsField
} from "@saleor/utils/filters/fields";
import { defineMessages, IntlShape } from "react-intl";

export enum ProductStockFilterKeys {
  brand = "brand",
  mall = "mall",
  updateDate = "updateDate",
  stockUpdateDate = "stockUpdateDate",
  productStatus = "productStatus",
  trackInventory = "trackInventory",
  vertical = "vertical"
}

export enum ProductStockStatus {
  DRAFT = "Draft",
  READY = "Ready",
  UNLISTED = "Unlisted",
  DELETED = "Deleted"
}
export enum TrackInventoryBoolean {
  true = "true",
  false = "false"
}
export interface ProductStockFilterOpts {
  brand: FilterOpts<string[]> & AutocompleteFilterOpts;
  mall: FilterOpts<string[]> & AutocompleteFilterOpts;
  updateDate: FilterOpts<MinMax>;
  stockUpdateDate: FilterOpts<MinMax>;
  productStatus: FilterOpts<string[]>;
  trackInventory: FilterOpts<string>;
  vertical: FilterOpts<string[]>;
}

const messages = defineMessages({
  brand: {
    defaultMessage: "Brand",
    description: "brand"
  },
  cyberzone: {
    defaultMessage: "Cyberzone",
    description: "cyberzone"
  },
  deleted: {
    defaultMessage: "Deleted",
    description: "deleted status"
  },
  draft: {
    defaultMessage: "Draft",
    description: "draft status"
  },
  false: {
    defaultMessage: "FALSE",
    description: "false"
  },
  fb: {
    defaultMessage: "Food and Beverages",
    description: "food and beverages"
  },
  grocery: {
    defaultMessage: "Grocery",
    description: "grocery"
  },
  mall: {
    defaultMessage: "Mall",
    description: "mall branch"
  },
  marketplace: {
    defaultMessage: "Marketplace",
    description: "marketplace"
  },
  productStatus: {
    defaultMessage: "Product Status",
    description: "product status"
  },
  ready: {
    defaultMessage: "Ready",
    description: "ready status"
  },
  retail: {
    defaultMessage: "Retail Affiliates",
    description: "retails affiliates"
  },
  stockUpdateDate: {
    defaultMessage: "Stock Update Date",
    description: "stock update date"
  },
  tenant: {
    defaultMessage: "Tenant",
    description: "tenant type"
  },
  trackInventory: {
    defaultMessage: "Track Inventory",
    description: "track inventorye"
  },
  true: {
    defaultMessage: "TRUE",
    description: "true"
  },
  unlisted: {
    defaultMessage: "Unlisted",
    description: "unlisted status"
  },
  updateDate: {
    defaultMessage: "Product Update Date",
    description: "product update "
  },
  vertical: {
    defaultMessage: "Vertical",
    description: "vertical"
  }
});

export function createFilterStructure(
  intl: IntlShape,
  opts: ProductStockFilterOpts
): IFilter<ProductStockFilterKeys> {
  return [
    {
      ...createDateField(
        ProductStockFilterKeys.updateDate,
        intl.formatMessage(messages.updateDate),
        opts.updateDate.value
      ),
      active: opts.updateDate.active
    },
    {
      ...createDateField(
        ProductStockFilterKeys.stockUpdateDate,
        intl.formatMessage(messages.stockUpdateDate),
        opts.stockUpdateDate.value
      ),
      active: opts.stockUpdateDate.active
    },
    {
      ...createAutocompleteField(
        ProductStockFilterKeys.brand,
        intl.formatMessage(messages.brand),
        opts.brand.value,
        opts.brand.displayValues,
        true,
        opts.brand.choices,
        {
          hasMore: opts.brand.hasMore,
          initialSearch: "",
          loading: opts.brand.loading,
          onFetchMore: opts.brand.onFetchMore,
          onSearchChange: opts.brand.onSearchChange
        }
      ),
      active: opts.brand.active
    },
    {
      ...createAutocompleteField(
        ProductStockFilterKeys.mall,
        intl.formatMessage(messages.mall),
        opts.mall.value,
        opts.mall.displayValues,
        true,
        opts.mall.choices,
        {
          hasMore: opts.mall.hasMore,
          initialSearch: "",
          loading: opts.mall.loading,
          onFetchMore: opts.mall.onFetchMore,
          onSearchChange: opts.mall.onSearchChange
        }
      ),
      active: opts.mall.active
    },
    {
      ...createOptionsField(
        ProductStockFilterKeys.productStatus,
        intl.formatMessage(messages.productStatus),
        opts.productStatus.value,
        true,
        [
          {
            label: intl.formatMessage(messages.draft),
            value: ProductStockStatus.DRAFT
          },
          {
            label: intl.formatMessage(messages.ready),
            value: ProductStockStatus.READY
          },
          {
            label: intl.formatMessage(messages.unlisted),
            value: ProductStockStatus.UNLISTED
          }
        ]
      ),
      active: opts.productStatus.active
    },
    {
      ...createOptionsField(
        ProductStockFilterKeys.trackInventory,
        intl.formatMessage(messages.trackInventory),
        [opts.trackInventory.value],
        false,
        [
          {
            label: intl.formatMessage(messages.true),
            value: TrackInventoryBoolean.true
          },
          {
            label: intl.formatMessage(messages.false),
            value: TrackInventoryBoolean.false
          }
        ]
      ),
      active: opts.trackInventory.active
    },
    {
      ...createOptionsField(
        ProductStockFilterKeys.vertical,
        intl.formatMessage(messages.vertical),
        opts.vertical.value,
        true,
        [
          {
            label: intl.formatMessage(messages.fb),
            value: intl.formatMessage(messages.fb)
          },
          {
            label: intl.formatMessage(messages.cyberzone),
            value: intl.formatMessage(messages.cyberzone)
          },
          {
            label: intl.formatMessage(messages.grocery),
            value: intl.formatMessage(messages.grocery)
          },
          {
            label: intl.formatMessage(messages.marketplace),
            value: intl.formatMessage(messages.marketplace)
          },
          {
            label: intl.formatMessage(messages.retail),
            value: intl.formatMessage(messages.retail)
          }
        ]
      ),
      active: opts.vertical.active
    }
  ];
}
